import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type MatchPublicView, type RegisteredGame } from '../api-client.js';
import { useAuthStore } from '../auth/auth-store.js';
import { LobbyCreator } from '../components/goldenballs/LobbyCreator.js';
import { useEventStream } from '../streams/use-event-stream.js';
import { useUserDisplayName } from '../users/use-user-profile.js';

/**
 * Public lobby — every match still accepting participants.
 *
 * Live-updates via the /api/streams/lobbies SSE channel: any
 * open/update/close event triggers a refetch (coalesced through a
 * 200ms debounce so a flurry of joins doesn't hammer the API).
 *
 * Hides the signed-in user's own pending matches — those live on
 * "Your matches".
 */
export default function LobbiesPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [lobbies, setLobbies] = useState<MatchPublicView[] | null>(null);
  const [games, setGames] = useState<RegisteredGame[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterGame, setFilterGame] = useState<string>('all');
  const [showCreator, setShowCreator] = useState(false);

  const refresh = useCallback(() => {
    if (!user) return;
    api
      .listLobbies({ excludeUserId: user.id })
      .then((data) => setLobbies(data))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [user]);

  // Initial load + games metadata for the filter chips.
  useEffect(() => {
    if (!user) return;
    refresh();
    api
      .listGames()
      .then((data) => setGames(data))
      .catch(() => {
        // Filter still works without metadata — chips just won't render.
      });
  }, [user, refresh]);

  // Live updates. Debounce because three players joining the same
  // game in the same tick would otherwise fire three refetches.
  useEventStream(
    user ? `/api/streams/lobbies` : null,
    useDebouncedRefresh(refresh, 200),
  );

  const filtered = useMemo(() => {
    if (!lobbies) return [];
    if (filterGame === 'all') return lobbies;
    return lobbies.filter((m) => m.match.gameKey === filterGame);
  }, [lobbies, filterGame]);

  const gameKeysInLobbies = useMemo(() => {
    if (!lobbies) return new Set<string>();
    return new Set(lobbies.map((m) => m.match.gameKey));
  }, [lobbies]);

  if (!user) {
    return <p className="text-ec-bone">Log in to browse lobbies.</p>;
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">Open lobbies</h2>
        <p className="font-serif text-sm text-ec-bone-dim">
          Matches still looking for an opponent. Updates live as games open and close.
        </p>
        <Link to="/" className="ml-auto font-display text-sm uppercase tracking-wide text-ec-brass hover:underline">
          ← back to games
        </Link>
      </header>

      {error && (
        <div className="ec-surface p-3 text-sm text-ec-face">
          {error}
        </div>
      )}

      {/* Golden Balls pregame. First-class, not hidden behind a
          disclosure triangle — this is the ONLY place the ball mode
          (including STING), bot count, anonymity and the rest can be
          set, so burying it meant players never saw a setting at all.
          The public queue still runs on defaults by design. */}
      {games?.some((game) => game.key === 'golden-balls') && (
        <section className="max-w-2xl">
          {showCreator ? (
            <LobbyCreator
              userId={user.id}
              onCreated={(matchId) => navigate(`/matches/${matchId}`)}
              onCancel={() => setShowCreator(false)}
            />
          ) : (
            <div className="ec-surface flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0">
                <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-face">
                  Golden Balls
                </h3>
                <p className="font-serif text-xs text-ec-bone-dim">
                  Set the rules yourself — Split or Steal, or three-ball with STING.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreator(true)}
                className="ec-btn ec-btn--primary ml-auto min-h-[2.75rem] px-4 text-sm"
              >
                New game — choose settings
              </button>
            </div>
          )}
        </section>
      )}

      {!lobbies && !error && (
        <p className="text-sm text-ec-bone-dim">Looking for open matches…</p>
      )}

      {lobbies && (
        <>
          {games && games.length > 1 && (
            <div className="flex flex-wrap gap-2">
              <FilterChip
                label="All games"
                active={filterGame === 'all'}
                onClick={() => setFilterGame('all')}
              />
              {games
                .filter((g) => gameKeysInLobbies.has(g.key))
                .map((g) => (
                  <FilterChip
                    key={g.key}
                    label={g.name}
                    active={filterGame === g.key}
                    onClick={() => setFilterGame(g.key)}
                  />
                ))}
              <span className="ml-auto self-center text-xs text-ec-bone-dim">
                {filtered.length} of {lobbies.length} open
              </span>
            </div>
          )}

          {filtered.length === 0 ? (
            <EmptyState totalOpen={lobbies.length} />
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((m) => (
                <LobbyCard key={m.match.id} match={m} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ec-btn !py-1.5 text-sm ${active ? '!border-ec-brass !text-ec-brass' : ''}`}
    >
      {label}
    </button>
  );
}

function EmptyState({ totalOpen }: { totalOpen: number }) {
  return (
    <div className="ec-surface p-6 font-serif text-sm text-ec-bone">
      {totalOpen === 0 ? (
        <>
          No open lobbies right now.{' '}
          <Link to="/" className="text-ec-brass hover:underline">
            Start a new match
          </Link>{' '}
          and someone may join.
        </>
      ) : (
        <>No open lobbies for that game.</>
      )}
    </div>
  );
}

function LobbyCard({ match }: { match: MatchPublicView }) {
  const creatorName = useUserDisplayName(match.match.createdByUserId);
  const seatsTotal = match.seats.length;
  const seatsTaken = match.participants.length;
  return (
    <li className="ec-surface flex flex-col p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="truncate font-display text-base font-medium uppercase tracking-wide text-ec-face">{match.game.name}</h3>
        <span className="rounded-full border border-ec-brass/60 bg-[rgba(245,168,28,0.18)] px-2 py-0.5 font-display text-[10px] font-semibold uppercase tracking-wider text-ec-brass">
          waiting
        </span>
      </div>
      <dl className="mt-3 space-y-1 text-xs">
        <div className="flex justify-between">
          <dt className="text-ec-bone-dim">Created by</dt>
          <dd className="truncate text-ec-bone">{creatorName}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ec-bone-dim">Seats</dt>
          <dd className="text-ec-bone">
            {seatsTaken} / {seatsTotal} taken
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-ec-bone-dim">Opened</dt>
          <dd className="text-ec-bone">{formatRelative(match.match.createdAt)}</dd>
        </div>
      </dl>
      <Link
        to={`/matches/${match.match.id}`}
        className="ec-btn ec-btn--primary mt-4 w-full"
      >
        Open lobby
      </Link>
    </li>
  );
}

/**
 * Returns a stable callback that, when invoked repeatedly within
 * `delayMs`, fires once at the trailing edge. Keeps SSE bursts from
 * triggering a thrash of refetches.
 */
function useDebouncedRefresh(fn: () => void, delayMs: number): () => void {
  const [ref] = useState({
    timer: null as ReturnType<typeof setTimeout> | null,
    fn,
  });
  ref.fn = fn;
  return useCallback(() => {
    if (ref.timer) clearTimeout(ref.timer);
    ref.timer = setTimeout(() => {
      ref.timer = null;
      ref.fn();
    }, delayMs);
  }, [ref, delayMs]);
}

/**
 * "30s ago", "5m ago", "2h ago", or the localised date for older
 * timestamps. Re-renders only when the parent does — fine here
 * because lobby items refetch on every change.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
